import React, { useMemo } from 'react';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import type { ErpInputTableActions } from '@/components/custom/erp';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { SelectedCellInfo, useTSCCPaste } from '../hooks';
import { FormFieldState } from '../../../hooks';
import { getTSCCTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface TSCCTabProps {
  formMode: FormMode;
  state: FormFieldState;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: MuaHangRecord) => void;
  onCellClick?: (params: DataTableRenderCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<MuaHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

export const TSCCTab: React.FC<TSCCTabProps> = ({
  formMode,
  state,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const columns = useMemo(
    () => getTSCCTableColumns(state, onCellValueChange, formMode),
    [state, onCellValueChange, formMode]
  );

  useTSCCPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const actions = useMemo<ErpInputTableActions>(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down')
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <ErpInputTableV2
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu={true}
      actions={actions}
      actionsDisabled={formMode === 'view'}
      actionLoading={isPasting}
      actionOrder={['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown']}
      pasteTarget={selectedCell ? { id: selectedCell.id, field: selectedCell.field } : null}
    />
  );
};
